import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {StudentModel} from '../../../models/authentication/student.model';
import {PlacementTestModel, PlacementTestSectionModel} from '../../../models/edu-admin/placement-test.model';
import {TranslateService} from '@ngx-translate/core';
import {
  PlacementTestAttemptModel,
  PlacementTestAttemptSectionQuestionModel,
  PlacementTestGroupAttempt
} from '../../../models/student/placement-test-attempt.model';
import {AppEvent, AppEventsService, AppEventType, ScrollEvent} from '../../../services/events/app-events.service';

@Component({
  selector: 'app-placement-group-view',
  templateUrl: './placement-group-view.component.html',
  styleUrls: ['./placement-group-view.component.css']
})
export class PlacementGroupViewComponent implements OnInit {

  constructor(public tSv: TranslateService,
              public appEventSv: AppEventsService) { }

  _students: StudentModel[] = [];
  @Input('students')
  set students(students: StudentModel[]) {
    this._students = students;
    console.log('num students: ' + students.length);
    this.setup();
  }

  get students(): StudentModel[] {

    return this._students;

  }

  highestNumSections = 0;
  _assessmentStructure: PlacementTestModel;
  @Input('assessmentStructure')
  set assessmentStructure(assessment: PlacementTestModel) {

    this._assessmentStructure = assessment;
    console.log('settings assessment structure: ' + assessment);

    if (assessment) {
      for (const section of assessment.sections) {
        if (this.highestNumSections < section.orderedQuestions.length) {
          this.highestNumSections = section.orderedQuestions.length;
        }
      }
    }

    this.setup();

  }

  _attempts: PlacementTestGroupAttempt[];
  get assessmentStructure(): PlacementTestModel {

    return this._assessmentStructure;
  }


  @Input('attempts')
  set attempts(attempts: PlacementTestGroupAttempt[]) {

    this._attempts = attempts;
    console.log('setting attempts: ' + attempts.length);
    this.setup();

  }

  get attempts(): PlacementTestGroupAttempt[] {
    return this._attempts;
  }

  sections: Record<string, SectionResult> = {};

  /**
   * For header calculation
   */
  topOffset = '0px';
  @ViewChild('header', { static: true }) skillHeader: ElementRef;
  @ViewChild('container', { static: true }) skillContainer: ElementRef;

  /**
   * Can be of value 'detailed' which gives critical information about passed values
   * and of type 'small' which gives detailed information on each section
   */
  @Input() layoutType = 'small';

  ngOnInit() {

  }

  getContainerWidth(): string {

    return (this.highestNumSections * 360).toString(10) + 'px';
  }

  setup() {

    if (this._assessmentStructure && this._attempts && this._students) {

      this.sections = {};
      for (const section of this.assessmentStructure.sections) {

        // Get all the section question attempts associated with that question
        const sectionQuestionAttempts = [];
        const placementTestAttempts = [];
        for (const attempt of this._attempts) {

          const filteredSections = attempt.placementTestAttempt.sections.filter((s) => s.sectionId === section._id);
          if (filteredSections.length > 0) {
            sectionQuestionAttempts.push(filteredSections);
          }

          placementTestAttempts.push(attempt.placementTestAttempt);

        }

        this.sections[section._id] = new SectionResult(section, placementTestAttempts);

      }

    }

  }

  sectionForId(sectionId: string): SectionResult {

    return this.sections[sectionId];

  }

  sectionQuestionForId(sectionId: string, questionId: string): SectionQuestionResult {

    const section = this.sections[sectionId];
    if (!section) {
      return null;
    }

    return section.groupQuestionAttempts[questionId];

  }

}

export class SectionResult {

  section: PlacementTestSectionModel;
  /**
   * The string qualifier is the question id and the section question result
   * grouped for all students not broken down.
   */
  groupQuestionAttempts: Record<string, SectionQuestionResult> = {};

  /**
   * Should store the student id with the section student result
   */
  studentAttempts: Record<string, object> = {};

  constructor(section: PlacementTestSectionModel, attempts: PlacementTestAttemptModel[]) {

    this.section = section;
    this.setupGroupAttempts(attempts);

  }

  /**
   * Sets up section question object with appropaite student data
   * @param placementAttempts All of the placement test attempts for all students in the group.
   */
  setupGroupAttempts(placementAttempts: PlacementTestAttemptModel[]) {

    const sectionQuestionIds = [];
    for (const orderedQuestion of this.section.orderedQuestions) {
      sectionQuestionIds.push(orderedQuestion.questionId);
    }

    /**
     * Go through all the attempts from the students and identify the correct
     * section and section questions attempted to create this section.
     */
    for (const attempt of placementAttempts) {

      // Find the correct section for the attempt of each student
      const filteredSectionAttempts = attempt.sections.filter((s) => s.sectionId === this.section._id);
      if (filteredSectionAttempts.length > 0) {

        const sectionAttempt = filteredSectionAttempts[0];

        // Create the section question results for each student
        for (const sectionQAttempt of sectionAttempt.orderedQuestions) {

          // Only go through the section question ids
          if (sectionQuestionIds.includes(sectionQAttempt.questionId)) {

            /**
             * Setup the section question result if it does not exist and add attempt to it.
             */
            let sectionQResult = this.groupQuestionAttempts[sectionQAttempt.questionId];
            if (!sectionQResult) {
              sectionQResult = new SectionQuestionResult(sectionQAttempt.questionId);
              this.groupQuestionAttempts[sectionQAttempt.questionId] = sectionQResult;
            }
            sectionQResult.addAttempt(sectionQAttempt);

          }
        }

      }

    }

  }

  avgSkillMasteryForSection(numStudents: number): string {

    if (numStudents === 0 || numStudents === null || numStudents === undefined) {
      return '0';
    }

    let totalMastery = 0;

    for (const questionId of Object.keys(this.groupQuestionAttempts)) {
      totalMastery += this.groupQuestionAttempts[questionId].mastery / numStudents;
    }

    return (totalMastery).toFixed(2);

  }

}

export class SectionQuestionResult {

  questionId: string;
  numFailed = 0;
  numPractising = 0;
  numMastered = 0;

  mastery = 0;

  questionAttempts: PlacementTestAttemptSectionQuestionModel[] = [];

  constructor(questionId: string) {
    this.questionId = questionId;
  }

  addAttempt(qAttempt: PlacementTestAttemptSectionQuestionModel) {

    if (qAttempt.questionId === this.questionId) {

      this.mastery += qAttempt.mastery;
      if (qAttempt.mastery === 0 && qAttempt.numWrong > 0) {
        this.numFailed += 1;
      } else if (qAttempt.mastery >= 0.49 && qAttempt.mastery <= 0.51) {
        this.numPractising += 1;
      } else if (qAttempt.mastery === 1) {
        this.numMastered += 1;
      }

      this.questionAttempts.push(qAttempt);

    }

  }

  numPassed(): number {

    return this.numPractising + this.numMastered;
  }

}

